// deps
import React, { ReactElement, memo, useCallback, useContext, useRef, useState, useMemo } from "react";

// internal-deps
import { LoadingScreen, PaymentModesAddNewCard as AddNewCard, ADD_NEW_CARD_TEXTS } from "@swiggy-private/payment-ui";
import { GenericError, Storage, useEffectOnce } from "@swiggy-private/core-ui";
import {
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    CheckoutRoute,
    getBridgeSdk,
    NEWRELIC_TABLES,
    recordNREvent,
    useNavigate,
    useSessionStorage,
    PublicApiContext,
} from "@swiggy-private/checkout-core";
import { ProceedNewCardDetails, VALID_PAYMENT_METHODS } from "@swiggy-private/payment-types";
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// components
import PaymentModesWrapper from "@payment-client/components/payment-modes/components/payment-modes-wrapper";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// styles
import styles from "./styles.scss";

// contexts
import { PaymentModesContext } from "@payment-client/contexts/PaymentModesContext";
import { ApiContext } from "@swiggy-private/checkout-core";

// constants
import { options as cardOptions } from "@payment-client/constants/Options";
import { HEADER_TITLE } from "@payment-client/constants/Header";
import { PAYMENT_MODES_STORAGE_KEYS } from "@payment-client/constants/PaymentModes";
import { PAYMENT_INITIAL_STATE } from "@payment-client";
import { SCREEN_NAME } from "@payment-client/analytics/constants";
import { CARD_API_KEYS } from "@swiggy-private/checkout-core";

// Analytics
import { onAddNewCardPayClickEvent, onAddNewCardScreenViewEvent } from "@payment-client/analytics/events/Card";

// helpers
import { getPaymentErrorScreenAction } from "@payment-client/helpers/Misc";
import { removeCardFingerPrint, canSecureCard } from "@payment-client/helpers/PaymentModeHelper";
import {
    CARD_BIN_NUMBER_MAX_LIMIT,
    SKIP_CLIENT_CARD_VALIDATION,
    defaultCardBrandDetails,
} from "@payment-client/constants/Card";
import { getCardDetails } from "@payment-client/apis/Card";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";
import { isCategoryInstamart } from "@payment-client/helpers/Misc";

// hooks
import useCardEvents from "@payment-client/hooks/UseCardEvents";

//types
import { CardBrandDetails } from "@swiggy-private/payment-types";
import { useCardHandlerPaymentModes } from "@payment-client/components/payment-modes/hooks/UseCardHandlerPaymentModes";
import { getCardDetailsPublic } from "@payment-client/apis/publicCard";

type AddNewCardProps = RouteProps<{
    isOfferEnabled: string;
    imageId: string;
}>;

export const PaymentModesAddNewCard = memo(function PaymentModeAddNewCard(
    _props: AddNewCardProps,
): ReactElement | null {
    const { getAllCardsResponse, loadingPaymentModes } = useContext(PaymentModesContext);
    const { screenViewEvent, clickEvent } = React.useContext(AnalyticsContext);
    const publicApiConfig = useContext(PublicApiContext);

    const [addCardDetails, setAddCardDetails] = useSessionStorage<ProceedNewCardDetails | undefined>(
        PAYMENT_MODES_STORAGE_KEYS.ADD_NEW_CARD,
        undefined,
    );

    const apiContextValue = useContext(ApiContext);
    const apiCallRef = useRef(false);
    const apiHost = apiContextValue.host.unwrap();

    const [cardBrandDetails, setCardBrandDetails] = useSessionStorage<CardBrandDetails>(
        PAYMENT_MODES_STORAGE_KEYS.CARD_DETAILS,
        defaultCardBrandDetails,
    );
    const [skipClientCardValidation, setSkipClientCardValidation] = useState(
        !!Storage.getItem(SKIP_CLIENT_CARD_VALIDATION),
    );

    const navigateTo = useNavigate();
    const { onSecureCardInfoClickAction, onSecureCardInfoView, onKnowMoreTokenizationClickAction } =
        useCardHandlerPaymentModes(SCREEN_NAME.ADD_NEW_CARD_PAYMENT_MODES);

    const { onProceedToPayClick } = useCardEvents(SCREEN_NAME.PAYMENT_MODES);

    const isPublicApiEnable = useMemo(
        () =>
            isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.CARDS_JUSPAY_APIS,
                CARD_API_KEYS.GET_CARD_DETAILS,
            ),
        [publicApiConfig],
    );

    const saveCardCtaHelperText = useMemo(
        () => (isCategoryInstamart() ? ADD_NEW_CARD_TEXTS.SAVE_CARD_ON_IM_PAYMENT_MODE : undefined),
        [],
    );

    const getCardDetailsByBin = useCallback(
        (cardBin: string) => {
            if (apiCallRef.current) {
                return;
            }
            if (cardBin.length < CARD_BIN_NUMBER_MAX_LIMIT) {
                setCardBrandDetails(defaultCardBrandDetails);
                return;
            }

            apiCallRef.current = true;

            const getCardDetailsFn = isPublicApiEnable ? getCardDetailsPublic : getCardDetails;

            getCardDetailsFn(apiHost, cardBin)
                .then((data) => {
                    apiCallRef.current = false;
                    if (data == null) {
                        return;
                    }

                    const result: CardBrandDetails = {
                        cardBrand: data.cardBrand,
                        cardIcon: { id: data.imageId || "", padding: 1 },
                        url: data.fullImageUrl,
                        tokenizeSupport: data.tokenizeSupport,
                        cardBin: data.bin,
                        disableForTenant: data.nonFoodDisabled || false,
                        disableForTenantReason: data.nonFoodDisabledReason || null,
                        enabled: data.enabled ?? true,
                        disableReason: data.disableReason || null,
                    };
                    setCardBrandDetails(result);

                    const shouldSkipClientCardValidation = data.skipClientCardValidation === true;
                    setSkipClientCardValidation(shouldSkipClientCardValidation);
                    Storage.setItem(SKIP_CLIENT_CARD_VALIDATION, shouldSkipClientCardValidation);
                })
                .catch(() => {
                    setCardBrandDetails(defaultCardBrandDetails);
                    apiCallRef.current = false;
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [apiHost, isPublicApiEnable],
    );

    const onProceed = useCallback(
        (details: ProceedNewCardDetails): void => {
            onAddNewCardPayClickEvent(SCREEN_NAME.PAYMENT_MODES, clickEvent);
            setAddCardDetails(details);
            removeCardFingerPrint();
            const isNonSecureCard = !canSecureCard(details, cardBrandDetails.cardBrand);
            navigateTo(CheckoutRoute.SECURE_CARD, {
                newCardDetails: details,
                paymentMethod: VALID_PAYMENT_METHODS.NEW_CARD,
                isNonSecureCard: isNonSecureCard,
            });
            return;
        },
        // only for setAddCardDetails Please make sure other dependency are present
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clickEvent, cardBrandDetails.cardBrand, navigateTo],
    );

    useEffectOnce(() => {
        getBridgeSdk()
            .setScreenCaptureState({ screenCapturingDisabled: true })
            .catch(() => void 0);

        onAddNewCardScreenViewEvent(SCREEN_NAME.PAYMENT_MODES, screenViewEvent);
        recordNREvent({
            eventType: NEWRELIC_TABLES.PAYMENT_MODES_ADD_NEW_CARD,
        });
    });

    if (loadingPaymentModes) {
        return (
            <PaymentModesWrapper title={HEADER_TITLE.ADD_NEW_CARD}>
                <LoadingScreen />
            </PaymentModesWrapper>
        );
    }

    if (getAllCardsResponse == null) {
        const action = getPaymentErrorScreenAction(PAYMENT_INITIAL_STATE.ADD_NEW_CARD);

        return (
            <PaymentModesWrapper title={HEADER_TITLE.ADD_NEW_CARD}>
                <GenericError action={action} />
            </PaymentModesWrapper>
        );
    }

    return (
        <PaymentModesWrapper title={HEADER_TITLE.ADD_NEW_CARD} containerClassName={styles.container}>
            <AddNewCard
                options={cardOptions}
                onProceed={onProceed}
                juspayTokenizationMeta={getAllCardsResponse.meta.tokenizationMeta}
                addCardDetails={addCardDetails}
                onSecureCardInfoView={onSecureCardInfoView}
                onSecureCardInfoClickAction={onSecureCardInfoClickAction}
                onKnowMoreTokenizationClickAction={onKnowMoreTokenizationClickAction}
                onCardNumberChange={getCardDetailsByBin}
                cardBrandDetails={cardBrandDetails}
                skipClientCardValidation={skipClientCardValidation}
                onProceedToPayClick={onProceedToPayClick}
                isOfferEnabled={_props.data?.isOfferEnabled || ""}
                imageId={_props.data?.imageId || ""}
                saveCardCtaHelperText={saveCardCtaHelperText}
            />
        </PaymentModesWrapper>
    );
});
